import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { fromEvent, Subject, timer } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import type { ModalControl } from 'components/organisms/modal';
import { ModalService } from 'components/organisms/modal';
import { EnvironmentService } from 'src/services/environment/environment.service';

import type { IPromoInstrumentEvent } from '../promo-instruments/promo-instruments.service';
import { PromoInstrumentService } from '../promo-instruments/promo-instruments.service';
import type { IExitIntentPopupOptions } from './entities/types';

@Injectable({ providedIn: 'root' })
export class ExitIntentPopupService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly cookieService = inject(CookieService);
    private readonly modalService = inject(ModalService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly promoInstrumentService = inject(PromoInstrumentService);

    private readonly COOKIE_NAME = 'tp-exit-intent-popup';
    private readonly COOKIE_VALUE = '1';
    private readonly COOKIE_EXPIRED_DAYS = 15;
    private readonly ACTIVATION_DELAY_MS = 10_000;
    private readonly PIXELS_FROM_TOP_WINDOW_EDGE_TO_TRIGGER = 10;
    private readonly USERCENTRICS_OVERLAY_ID = 'usercentrics-root';
    private readonly deactivated$$ = new Subject<void>();
    private options: IExitIntentPopupOptions | null = null;
    private modalControl: ModalControl | null = null;

    public init(options: IExitIntentPopupOptions): void {
        if (isPlatformBrowser(this.platformId)) {
            this.deactivated$$.next(null);

            if (this.checkCouldBeActivated()) {
                this.activate(options);
            }
        }
    }

    public subscribeToPromoInstrumentEvents(options: IExitIntentPopupOptions): void {
        if (!options?.data?.id || this.options?.data?.id === options.data.id) return;

        this.options = options;
        this.promoInstrumentService.observePromoInstrument(this.options.data.id, (params: IPromoInstrumentEvent) => {
            if (params) {
                if (params.data?.layout) {
                    this.options = { ...this.options, data: params.data.layout?.exit_popup };
                }
                if (this.options) this.openPopup(this.options);
            } else {
                this.modalControl?.close();
            }
        });
    }

    private activate(options: IExitIntentPopupOptions): void {
        const componentsPromise = this.loadExitIntentComponents();

        timer(this.ACTIVATION_DELAY_MS)
            .pipe(
                takeUntil(this.deactivated$$),
                takeUntil(options.onDestroy$),
                switchMap(() =>
                    fromEvent(window, 'mouseout').pipe(
                        filter(
                            (event: MouseEvent & { toElement: EventTarget }) =>
                                !event.toElement &&
                                !event.relatedTarget &&
                                event.clientY < this.PIXELS_FROM_TOP_WINDOW_EDGE_TO_TRIGGER &&
                                (event.target as Element)?.id !== this.USERCENTRICS_OVERLAY_ID
                        ),
                        takeUntil(this.deactivated$$),
                        takeUntil(options.onDestroy$)
                    )
                )
            )
            .subscribe(async () => {
                const [{ ExitIntentPopupComponent }, { ExitIntentPopupModalContainerComponent }] =
                    await componentsPromise;

                this.deactivated$$.next();
                this.modalControl = this.modalService.open(
                    ExitIntentPopupComponent,
                    { container: ExitIntentPopupModalContainerComponent },
                    options.data
                );
                this.modalControl.beforeClosing$.subscribe(() => {
                    this.saveUserCloseInteraction();
                });
            });
    }

    private openPopup(options: IExitIntentPopupOptions) {
        this.modalControl?.close();
        return this.loadExitIntentComponents().then(
            ([{ ExitIntentPopupComponent }, { ExitIntentPopupModalContainerComponent }]) => {
                this.modalControl = this.modalService.open(
                    ExitIntentPopupComponent,
                    { container: ExitIntentPopupModalContainerComponent },
                    options.data
                );
            }
        );
    }

    private loadExitIntentComponents(): Promise<any> {
        return Promise.all([
            import('components/organisms/exit-intent-popup/exit-intent-popup.component'),
            import('components/organisms/exit-intent-popup/modal-container/exit-intent-popup-modal-container.component'),
        ]);
    }

    private checkCouldBeActivated(): boolean {
        return this.cookieService.get(this.COOKIE_NAME) !== this.COOKIE_VALUE;
    }

    private saveUserCloseInteraction(): void {
        this.cookieService.set(this.COOKIE_NAME, this.COOKIE_VALUE, {
            path: '/',
            expires: this.COOKIE_EXPIRED_DAYS,
            domain: this.environmentService.environment.cookieDomain,
        });
    }
}
